import { useState } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Button from '@/components/homepage/Button';
import beTwMerge from '@/utils/beTwMerge';
import { ColorKeys } from '@/styles/colors';
import { getFaqSchema } from '@/utils/seo';
import { getInnerText } from '@/utils/utils';

export interface FAQItem {
  question: string;
  answer: React.ReactNode; // Use React.ReactNode for complex JSX elements
}

export type VariantType = 'default' | 'faq';

export default function ReportsSection({
  heading,
  body,
  boxes,
  faqData,
  classNames,
  variant = 'default',
}: {
  heading?: string;
  body?: string;
  boxes?: {
    title: string;
    classNames?: string;
    action: { label: string; type: 'primary' | 'secondary' | 'link'; href?: string; color: ColorKeys };
  }[];
  faqData: FAQItem[];
  classNames?: string;
  variant?: VariantType;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openFAQ, setOpenFAQ] = useState(true);
  const isFaq = variant === 'faq';
  const FaqHeaderTag = isFaq ? 'h2' : 'p';
  const FaqQuestionTag = isFaq ? 'h3' : 'div';

  const items = faqData.map(item => ({ question: { content: item.question }, answer: getInnerText(item.answer) }));
  const scheme = getFaqSchema(items);

  const router = useRouter();
  const currentPath = router.asPath;

  const handleToggle = (index: number, item: any) => {
    (window as any).dataLayer.push({
      event: 'faq_click',
      faq_page_url: currentPath,
      faq_action: openIndex === index ? 'faq collapse' : 'faq expand',
      faq_label: item.question,
    });
    setOpenIndex(prevIndex => (prevIndex === index ? null : index));
  };

  const handleToggleTitle = () => {
    if (isFaq) {
      setOpenFAQ(!openFAQ);
    }
  };
  const hoverColor = {
    'tw-bg-color-dark-green-middle': 'hover:tw-text-color-light-green-middle',
    'tw-bg-color-light-green-light-middle': 'hover:tw-text-color-dark-green',
  };
  const getHoverClass = (buttonClassNames: string) =>
    buttonClassNames
      ?.split(' ')
      .map(cn => hoverColor[cn])
      .filter(Boolean)
      .join(' ') || '';
  return (
    <>
      {isFaq && (
        <Head>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: scheme }} />
        </Head>
      )}
      <div className={beTwMerge('tw-flex tw-flex-col tw-items-center tw-bg-color-grey-20', classNames)}>
        <div
          className={beTwMerge(
            'tw-w-full tw-flex tw-flex-col tw-items-stretch tw-max-w-[1440px] tw-justify-center tw-gap-[32px] lg:tw-gap-[64px] tw-py-[40px] lg:tw-py-[86px]',
            isFaq && 'tw-gap-0 lg:tw-gap-0'
          )}
        >
          {heading && (
            <div className="tw-flex tw-flex-col tw-items-center tw-self-center tw-w-full lg:tw-w-[910px]">
              <h2 className="tw-mb-0 tw-text-[22px] lg:tw-text-[32px] tw-font-heavy tw-uppercase">{heading}</h2>
            </div>
          )}
          {boxes && (
            <div className="tw-w-full tw-flex tw-justify-center tw-flex-col lg:tw-flex-row">
              {boxes?.map((box, index) => (
                <div
                  key={index}
                  className={beTwMerge(
                    'tw-flex tw-flex-col tw-items-center tw-p-[40px] lg:tw-py-[109px] tw-justify-center tw-w-full tw-gap-4 lg:tw-gap-8 lg:tw-flex-1 lg:tw-h-auto tw-bg-color-dark-rose',
                    box.classNames
                  )}
                >
                  <h4 className="tw-text-color-white tw-text-center tw-my-0 tw-font-ebgaramond tw-font-normal tw-text-[30px] lg:tw-text-[48px]">
                    {box.title}
                  </h4>
                  <Button
                    className="tw-text-[22px] tw-font-roman lg:tw-font-avenirmedium lg:tw-text-[28px]"
                    color={box.action.color}
                    label={box.action.label}
                    type={box.action.type}
                    href={box.action.href}
                  />
                </div>
              ))}
            </div>
          )}
          <FaqHeaderTag
            className={beTwMerge(
              'tw-font-base tw-text-center tw-mb-0 tw-self-center tw-w-full tw-p-[45px_16px_15px] tw-text-[16px] lg:tw-p-0 lg:tw-text-[28px] lg:tw-font-avenirmedium lg:tw-w-[800px]',
              isFaq ? 'tw-inline-flex tw-items-center tw-justify-between md:tw-justify-center tw-gap-x-4 tw-mb-0' : 'h3'
            )}
            onClick={handleToggleTitle}
          >
            {body}
            {isFaq && (
              <svg
                data-accordion-icon
                className={beTwMerge('tw-w-[20px] tw-h-[10px] tw-shrink-0', !openFAQ && 'tw-rotate-[-180deg]')}
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M9 5 5 1 1 5"
                />
              </svg>
            )}
          </FaqHeaderTag>
          <div
            className={beTwMerge(
              'tw-overflow-hidden tw-transition-[all] tw-duration-[500ms] tw-ease-in-out tw-px-4 lg:tw-px-[150px]',
              !openFAQ && isFaq && 'tw-max-h-[1px]'
            )}
          >
            {faqData.map((item, index) => (
              <div key={index} className="">
                <FaqQuestionTag
                  className={beTwMerge(
                    'tw-my-4 tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none tw-text-be-lg tw-font-avenirmedium',
                    isFaq && 'tw-font-base'
                  )}
                  onClick={() => handleToggle(index, item)}
                >
                  <span
                    className={beTwMerge(
                      'tw-text-base lg:tw-text-lg tw-font-medium tw-leading-[normal] lg:tw-leading-[normal]'
                    )}
                  >
                    {item.question}
                  </span>
                  <svg
                    data-accordion-icon
                    className={beTwMerge(
                      'tw-w-[12px] tw-h-[6px] tw-shrink-0',
                      openIndex !== index && 'tw-rotate-[-180deg]'
                    )}
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5 5 1 1 5"
                    />
                  </svg>
                </FaqQuestionTag>

                <div
                  className={beTwMerge(
                    'tw-border-0 tw-border-b-[1px] tw-border-solid tw-border-black tw-overflow-hidden tw-transition-[all] tw-duration-450',
                    openIndex === index ? 'tw-max-h-[1500px] tw-pb-4' : 'tw-max-h-[1px]'
                  )}
                >
                  {item.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
